import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import Skeleton from 'react-loading-skeleton';
import { startOfDay, format } from 'date-fns';
import { IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { PrimaryActionButton } from 'components/ActionButton';

import useImageFetcher from 'lib/hooks/useImageFetcher';
import { findColorType, formatTitleByWord, findValidFor } from 'lib/uiHelpers';
import { rupiah } from 'lib/currency';

const ModalDatePicker = ({ open, onClose, item }) => {
  const navigation = useNavigate();
  const [selectedDate, setSelectedDate] = useState();
  const { id, images, package_name, type, category, price, credit_number } = item;
  const { imageUrls, loading } = useImageFetcher(images, true);
  const today = startOfDay(new Date());

  const baseDisabled = [{ before: today }];

  let disabledDays = baseDisabled;

  if (type?.toLowerCase() === 'weekend') {
    disabledDays = [...baseDisabled, { dayOfWeek: [1, 2, 3, 4, 5] }];
  } else if (type?.toLowerCase() === 'weekday') {
    disabledDays = [...baseDisabled, { dayOfWeek: [0, 6] }];
  }

  if (!open || !item) return null;

  const goToCheckout = (paramsId) => {
    navigation(`/checkout?package_type=event_package&package_id=${paramsId}`);
  };

  const sessionInfo =
    credit_number && credit_number > 0
      ? `${credit_number} ${credit_number > 1 ? 'Sessions' : 'Session'}`
      : '-';

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 text-kin-text p-4">
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-[720px] p-5 grid grid-cols-2 gap-5">
        <button
          aria-label="Close modal"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 active:bg-gray-300 transition">
          <IconX alt="Close" className="w-4 h-4" />
        </button>

        <div className="flex flex-col gap-4">
          <div className="relative aspect-[3/2] w-full overflow-hidden rounded-2xl">
            {loading ? (
              <Skeleton height="100%" width="100%" className="rounded-2xl" />
            ) : (
              <img
                alt={package_name}
                src={imageUrls?.[0] ?? ''}
                className="w-full h-full object-cover object-center"
              />
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex justify-end">
              <span
                className={`px-3 py-0.5 rounded-2xl text-xs font-semibold text-white capitalize ${findColorType(
                  type?.toLowerCase(),
                )}`}>
                {type ?? '-'}
              </span>
            </div>

            <p className="text-sm font-light">
              {category === 1 ? 'Exhibition + Play' : 'Exhibition Only'}
            </p>

            <h3 className="capitalize text-lg font-semibold leading-snug">
              {formatTitleByWord(package_name ?? '-')}
            </h3>

            <p className="text-xl font-bold">{rupiah(price ?? 0)}</p>

            <ul className="bg-[#F3F2F0] p-3 rounded-lg flex flex-col gap-y-1 text-sm">
              <li className="flex items-center gap-2">
                <img src="/kin/assest/image/icon-star.svg" alt="" className="w-4 h-4" />
                {findValidFor(category)}
              </li>
              <li className="flex items-center gap-2">
                <img src="/kin/assest/image/icon-calender.svg" alt="" className="w-4 h-4" />
                {sessionInfo}
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col h-full">
          <h4 className="text-base font-semibold text-center mb-2">Select Date</h4>

          <div className="flex-1 flex justify-center items-center">
            <DayPicker
              mode="single"
              selected={selectedDate}
              disabled={disabledDays}
              onSelect={setSelectedDate}
              modifiersClassNames={{
                selected: 'bg-primary-main text-white',
                today: 'text-primary-main font-semibold',
              }}
            />
          </div>

          <PrimaryActionButton
            fullWidth
            disabled={!selectedDate}
            onClick={() => {
              if (selectedDate) {
                goToCheckout(id, selectedDate);
              }
            }}
            label={selectedDate ? `BOOK FOR ${format(selectedDate, 'dd MMM yyyy')}` : 'SELECT DATE'}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalDatePicker;
